import { useEffect, useState } from "react";
import {
  ArrowUpTrayIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline"; // Icons
import axios from "axios";
import { Modal } from "../../components/ui/modal"; // Import Modal component
import { CheckCircle } from "lucide-react"; // Import CheckCircle icon

interface FileUploadProps {
  onFileSelect: (file: File | null) => void;
  profileImageUrl?: string;
  profileImageName?: string;
  showDelete?: boolean;
  userId?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  profileImageUrl,
  profileImageName,
  showDelete = true,
  userId,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [showUploadPrompt, setShowUploadPrompt] = useState<boolean>(true);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false); // State for delete success modal

  useEffect(() => {
    if (profileImageUrl) {
      setShowUploadPrompt(false);
    }
  }, [profileImageUrl]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      onFileSelect(selectedFile);
      setShowUploadPrompt(false);
    }
  };

  const removeFile = async () => {
    if (!showDelete) return;

    const token = sessionStorage.getItem("access_token");

    if (!token || !userId) {
      console.error("Token or userId is missing");
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/user/delete-image`,
        { user_id: userId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === 200) {
        // On success, clear the file and show delete success modal
        setFile(null);
        onFileSelect(null);
        setShowUploadPrompt(true);
        setDeleteModalOpen(true); // Show delete success modal
      } else {
        console.error("Failed to delete image:", response.data.message);
      }
    } catch (error) {
      console.error("Error deleting image:", error);
    }
  };

  return (
    <div className="w-full max-w-sm md:max-w-md lg:max-w-lg mx-auto">
      <div
        className={`bg-white rounded-2xl shadow-md dark:bg-gray-900 dark:border-gray-600 w-full h-56 sm:h-64 md:h-[230px] flex items-center justify-center relative ${file || profileImageUrl
            ? "border border-gray-300"
            : "border border-gray-300 border-dashed"
          }`}
      >
        {!showUploadPrompt && (file || profileImageUrl) ? (
          <>
            {/* Profile Image Container */}
            <div className="relative w-[200px] h-[200px] overflow-hidden rounded-lg shadow-md">
              {/* Profile Image */}
              <img
                src={file ? URL.createObjectURL(file) : profileImageUrl}
                alt="Profile Preview"
                className="w-full h-full object-cover"
              />

              {/* Edit & Delete Icons (Bottom Overlay) */}
              <div className="absolute bottom-0 left-0 w-full bg-black bg-opacity-50 text-white py-2 flex items-center justify-center gap-2">
                <label htmlFor="fileInput" className="cursor-pointer">
                  <PencilSquareIcon className="h-6 w-6 text-white" />
                </label>
                {showDelete && (
                  <button type="button" onClick={removeFile} className="cursor-pointer">
                    <TrashIcon className="h-6 w-6 text-white" />
                  </button>
                )}
              </div>
            </div>


            {profileImageName && (
              <p className="absolute bottom-1 text-sm text-gray-600 dark:text-gray-300 mt-2">
                {profileImageName}
              </p>
            )}

            <input
              type="file"
              className="hidden"
              id="fileInput"
              accept="image/*"
              onChange={handleFileChange}
            />
          </>
        ) : (
          <label
            htmlFor="fileInput"
            className="cursor-pointer flex flex-col justify-center items-center w-full h-full bg-white border border-gray-300 border-dashed rounded-2xl dark:bg-gray-900 dark:border-gray-600 p-4 sm:p-6"
          >
            <div className="text-center">
              <input
                type="file"
                className="hidden"
                id="fileInput"
                accept="image/*"
                onChange={handleFileChange}
              />

              <div className="inline-flex justify-center items-center w-12 h-12 sm:w-14 sm:h-14 bg-blue-100 text-blue-600 rounded-full dark:bg-blue-900 dark:text-blue-300 shadow-md">
                <ArrowUpTrayIcon className="h-8 w-8 sm:h-10 text-blue-500" />
              </div>

              <p className="mt-3 text-base sm:text-theme-xs font-bold text-gray-800 dark:text-white">
                Upload Your Profile Image
              </p>

              <p className="text-theme-sm text-gray-500 dark:text-gray-400 mt-2">
                Drag and drop your PNG, JPG, WebP, SVG images here or browse
              </p>

              <p className="text-blue-600 underline text-sm mt-3">
                Browse File
              </p>
            </div>
          </label>
        )}
      </div>

      {/* Delete Success Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
      >
        <button
          onClick={() => setDeleteModalOpen(false)}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        ></button>

        <div className="flex justify-center">
          <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>

        <h4 className="text-lg font-bold text-gray-800 dark:text-white mt-4">
          Image Deleted Successfully
        </h4>

        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          The profile image has been deleted successfully.
        </p>

        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setDeleteModalOpen(false)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            OK
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default FileUpload;